/**
 * UndoToast — bottom-of-screen 30s undo affordance (Block 6.B.1 / ADR-017
 * cross-cutting #3 + F-O-1).
 *
 * Surfaces only the most-recent undo entry per F-O-1; subsequent saves
 * replace the toast. After 30s the toast hides; the entry stays in the
 * stack (UI-invisible until PR-3 edit-history surface lands).
 *
 * Non-modal; doesn't block interaction (per ADR-017 F1 spirit).
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useUndo } from '@/lib/undo';
import { endSentence } from '@/lib/sentence';
import { FLOATING_BOX_CLS } from '@/components/FloatingNotices';

const TOAST_VISIBLE_MS = 30_000;

export function UndoToast(): ReactNode {
  const { entries, invoke, dismiss } = useUndo();
  const [visibleId, setVisibleId] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  // F-O-1: surface only the most-recent entry. When entries change,
  // bind the visibleId to the newest; clear on timer expiry.
  useEffect(() => {
    if (entries.length === 0) {
      setVisibleId(null);
      return;
    }
    const newest = entries[entries.length - 1]!;
    if (newest.id !== visibleId) {
      setVisibleId(newest.id);
    }
  }, [entries, visibleId]);

  useEffect(() => {
    if (visibleId === null) return;
    const t = setTimeout(() => {
      // Hide UI; entry remains in stack.
      setVisibleId(null);
    }, TOAST_VISIBLE_MS);
    return () => clearTimeout(t);
  }, [visibleId]);

  if (visibleId === null) return null;
  const entry = entries.find((e) => e.id === visibleId);
  if (!entry) return null;

  async function handleUndo(): Promise<void> {
    setPending(true);
    try {
      await invoke(entry!.id);
    } finally {
      setPending(false);
      setVisibleId(null);
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="undo-toast"
      className={`${FLOATING_BOX_CLS} flex items-center justify-between gap-3`}
    >
      <span className="text-sm">{endSentence(entry.label)} Saved.</span>
      <span className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => void handleUndo()}
          disabled={pending}
          className="min-h-11 min-w-11 px-3 py-1 text-sm font-medium underline disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          {pending ? 'Undoing…' : 'Undo'}
        </button>
        <button
          type="button"
          onClick={() => dismiss(entry.id)}
          aria-label="Dismiss"
          className="min-h-11 min-w-11 px-2 py-1 text-sm text-sage-200 hover:text-cream focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
        >
          ×
        </button>
      </span>
    </div>
  );
}
